import React, { FC, useEffect, useState } from "react";

import {
    FormWidget,
    FormWidgetConfig,
    FormWidgetEnv,
    FormWidgetSubHeader,
    WidgetStylesheet,
    useFormPageMarker,
} from "@xapp/form-widget";

import { invertColor, sameColor } from "./utils/color";
import { PartnerDetails } from "./utils/partnerDetails";

export interface ContactPageProps extends Pick<FormWidgetEnv, "partnerId" | "businessName"> {
    /**
     * URL of the business logo, displayed in the header
     */
    businessLogoUrl?: string;
    /**
     * What the header draws: the business's logo, or its name as text.
     *
     * A partner decision, resolved from `formStyle.framing.headerDisplay` and
     * handed down by App. Absent means "logo" -- all this header has ever drawn
     * -- so no partner changes appearance by upgrading.
     */
    headerDisplay?: "logo" | "name";
    /**
     * The band under the header, carrying the relationship rather than the
     * business — see FormWidgetEnv.subHeader for why it rides on the app's own
     * config instead of a partner record.
     */
    subHeader?: FormWidgetSubHeader;
    /**
     * Address of the business, displayed in the header
     *
     */
    businessAddress?: string;
    /**
     * Website of the business, displayed in the footer
     */
    businessWebsite?: string;

    config: FormWidgetConfig;

    /**
     * Partner details (fetched from API or legacy hardcoded)
     */
    partnerDetails?: PartnerDetails;

    theme?: {
        backgroundColor?: string;
        headerBackgroundColor?: string;
        headerBusinessNameColor?: string;
    };
    /**
     * Disables the "Powered by XAPP AI" link in the footer
     */
    disablePoweredBy?: boolean;
    /**
     * Custom message for the "powered by" footer text
     */
    poweredByMessage?: string;
    /**
     * Custom URL for the "powered by" footer link
     */
    poweredByUrl?: string;
    /**
     * Callback invoked when the user submits the form. Used to fire external
     * conversion tracking (e.g. a Meta Pixel `Lead` event). Receives no
     * arguments by design — no form PII is exposed to the caller.
     */
    onSubmit?: () => void;
}

/** invertColor, for a colour we were handed rather than one we computed. */
function safeInvert(color: string): string | undefined {
    try {
        return invertColor(color, true);
    } catch {
        return undefined;
    }
}

/**
 * Stand alone contact page that uses the FormWidget
 *
 * You must also include WidgetStylesheet on the same view/page as the ContactPage to successfully style the widget.
 * @param props
 * @returns
 */
export const ContactPage: FC<ContactPageProps> = (props) => {
    const {
        config,
        businessAddress,
        businessName,
        businessWebsite,
        businessLogoUrl,
        headerDisplay,
        subHeader,
        disablePoweredBy,
        partnerId,
        partnerDetails,
        theme,
        poweredByMessage,
        poweredByUrl,
        onSubmit,
    } = props;

    const { backgroundColor, headerBackgroundColor, headerBusinessNameColor } = theme || {};

    // This page IS the form - it renders a docked FormWidget as its entire content. Say so,
    // so the chat widget injected over the top by `?chatWidgetKey=` stops offering Contact
    // and Book buttons into the form already filling the screen, and stops pulling in
    // form.xapp.ai for a second instance with its own side button.
    // XappMedia/chat-widget#1626.
    useFormPageMarker();

    // Whichever the partner asked for, and only if we actually have it. The
    // header used to render whenever a name OR a logo existed while only ever
    // drawing the logo, so a business with a name and no logo got an empty
    // coloured bar.
    const showName = headerDisplay === "name" && !!businessName;
    const showLogo = headerDisplay !== "name" && !!businessLogoUrl;

    // Same rule the header above applies to the business name: a colour that is
    // absent, or indistinguishable from what it sits on, is not a colour -- it
    // is an invisible line. Only when a background was configured; without one
    // there is nothing to guarantee a contrast against, and the band inherits
    // the page rather than being given a colour chosen for a different surface.
    //
    // The inversion is guarded because `subHeader` arrives straight off
    // config.json with no schema check, and invertColor throws on anything that
    // is not a 3- or 6-digit hex. There is no error boundary on this page, so an
    // unguarded throw would take the whole page down -- form and all -- for one
    // mistyped colour, which is far worse than the empty strip the band's own
    // rules exist to avoid. A background we cannot invert leaves whatever text
    // colour the app asked for: no contrast to compute, and no basis to
    // second-guess it.
    const subHeaderTextColor = subHeader?.backgroundColor
        ? subHeader.textColor && !sameColor(subHeader.textColor, subHeader.backgroundColor)
            ? subHeader.textColor
            : safeInvert(subHeader.backgroundColor) || subHeader.textColor
        : subHeader?.textColor;

    return (
        <div className="App" style={{ backgroundColor }}>
            {showName || showLogo ? (
                <div className="Header" style={{ backgroundColor: headerBackgroundColor }}>
                    {showLogo && (
                        <img className={"BusinessLogo"} src={businessLogoUrl} alt={businessName} />
                    )}
                    {showName && (
                        <p className={"BusinessName"} style={{ color: headerBusinessNameColor }}>
                            {businessName}
                        </p>
                    )}
                    {businessAddress && (
                        <p className={"BusinessAddress"} style={{ color: headerBusinessNameColor }}>
                            {businessAddress}
                        </p>
                    )}
                </div>
            ) : null}
            {/* Under the header, not beside it. The header is the business's;
                this is the relationship, so the two read in sequence rather
                than competing for one bar. Skipped entirely when there is
                nothing to draw -- colours alone are an empty coloured strip,
                which is the bug the header itself used to have. */}
            {subHeader && (subHeader.text || subHeader.logoUrl) ? (
                <div
                    className="SubHeader"
                    style={{
                        backgroundColor: subHeader.backgroundColor,
                        color: subHeaderTextColor,
                    }}>
                    {subHeader.logoUrl && (
                        <img
                            className="SubHeaderLogo"
                            src={subHeader.logoUrl}
                            // Decorative beside text that already says it. Alone
                            // the logo IS the credential, and alt="" would delete
                            // the band for anyone not looking at it.
                            alt={subHeader.text ? "" : businessName ? `${businessName} partner` : "Partner logo"}
                        />
                    )}
                    {subHeader.text && <span className="SubHeaderText">{subHeader.text}</span>}
                </div>
            ) : null}
            <div className="Content">
                <FormWidget config={config} docked={true} onSubmit={onSubmit} />
            </div>
            <div className="Footer" style={{ ...partnerDetails?.style?.footer }}>
                <div className="BusinessInfo">
                    {businessName && <b>{businessName}</b>}
                    {businessAddress && !partnerDetails && <p>{businessAddress}</p>}
                    {businessWebsite && <a href={businessWebsite}>Visit Our Website</a>}
                </div>
                {!disablePoweredBy && !partnerDetails && (
                    <a className={"XAPP"} target="_blank" href={poweredByUrl || "https://xapp.ai"}>
                        {poweredByMessage || "Powered by XAPP AI"}
                    </a>
                )}
                {partnerDetails && (
                    <div className={"PartnerInfo"}>
                        <p className={"PoweredBy"}>Powered by</p>
                        {partnerDetails?.partnerWebsite ? (
                            <a
                                className={"Partner"}
                                target="_blank"
                                href={partnerDetails.partnerWebsite}
                            >
                                {partnerDetails.partnerLogoUrl ? (
                                    <img
                                        className={"PartnerLogo"}
                                        src={partnerDetails.partnerLogoUrl}
                                        alt={partnerDetails.partnerName}
                                    />
                                ) : (
                                    <span>{partnerDetails.partnerName}</span>
                                )}
                            </a>
                        ) : partnerDetails.partnerLogoUrl ? (
                            <img
                                className={"PartnerLogo"}
                                src={partnerDetails.partnerLogoUrl}
                                alt={partnerDetails.partnerName}
                            />
                        ) : (
                            <span>{partnerDetails.partnerName}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
